import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { VerifyEmailPage } from './features/auth/VerifyEmailPage';
import { AppShell } from './features/shell/AppShell';
import type { SessionValue } from './lib/session';

export type RouterContext = { session: SessionValue };

const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

/** Only paths inside the app (never "//host" or absolute URLs). */
const safePath = (value: unknown) => {
  const path = text(value);
  return path?.startsWith('/') && !path.startsWith('//') ? path : undefined;
};

function NotFound() {
  return (
    <div style={{ padding: 48 }}>
      <h1>Pagina non trovata</h1>
      <p style={{ marginTop: 8 }}>
        <Link to="/oggi">Torna a oggi</Link>
      </p>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

// Signed-out pages: a signed-in visitor goes straight to the app.
const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'guest',
  beforeLoad: ({ context, location }) => {
    if (context.session.status === 'signedIn') {
      const search = location.search as { redirect?: unknown };
      throw redirect({ to: safePath(search.redirect) ?? '/oggi' });
    }
  },
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { error?: string; redirect?: string } => ({
    error: text(search.error),
    redirect: safePath(search.redirect),
  }),
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: '/registrati',
  component: RegisterPage,
});

const forgotRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: '/password-dimenticata',
  component: ForgotPasswordPage,
});

// Opened from email links, with or without a session (paths fixed by the API's emails).
const tokenSearch = (search: Record<string, unknown>): { token?: string } => ({
  token: text(search.token),
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: tokenSearch,
  component: ResetPasswordPage,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  validateSearch: tokenSearch,
  component: VerifyEmailPage,
});

// The app itself: signed-out visitors go to the login, then come back here.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    const { session } = context;
    if (session.status !== 'signedIn') {
      // Come back to the requested page after signing in, but not after choosing "Esci".
      const manual = session.status === 'signedOut' && session.reason === 'manual';
      throw redirect({
        to: '/login',
        search: { redirect: manual || location.pathname === '/' ? undefined : location.href },
      });
    }
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/oggi' });
  },
});

// Each section is its own chunk, loaded on first visit.
const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/oggi',
  component: lazyRouteComponent(() => import('./features/today/TodayPage'), 'TodayPage'),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/attivita',
  // ?scheda=<id> opens a card: the link that "Copia il link" gives.
  validateSearch: (search: Record<string, unknown>): { scheda?: string } => ({
    scheda: text(search.scheda),
  }),
  component: lazyRouteComponent(() => import('./features/tasks/TasksPage'), 'TasksPage'),
});

const moneyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/finanze',
  // ?sezione=wealth opens a tab: where what is due gets settled.
  validateSearch: (search: Record<string, unknown>): { sezione?: string } => ({
    sezione: ['wealth', 'plans', 'forecast'].includes(String(search.sezione))
      ? String(search.sezione)
      : undefined,
  }),
  component: lazyRouteComponent(() => import('./features/money/MoneyPage'), 'MoneyPage'),
});

/** The section was called "Soldi": old links and bookmarks still work. */
const oldMoneyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/soldi',
  beforeLoad: () => {
    throw redirect({ to: '/finanze' });
  },
});

const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendario',
  // ?vista=giorno&giorno=2026-10-27 opens the timeline of a day; ?google=… is how the
  // connection to Google Calendar went.
  validateSearch: (
    search: Record<string, unknown>,
  ): { giorno?: string; vista?: string; google?: string } => ({
    giorno: /^\d{4}-\d{2}-\d{2}$/.test(String(search.giorno)) ? String(search.giorno) : undefined,
    vista: search.vista === 'giorno' ? 'giorno' : undefined,
    google: ['collegato', 'errore', 'annullato', 'non-attivo'].includes(String(search.google))
      ? String(search.google)
      : undefined,
  }),
  component: lazyRouteComponent(() => import('./features/calendar/CalendarPage'), 'CalendarPage'),
});

const focusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/focus',
  // ?scheda=<id>&minuti=60 prepares a session for a card: the link of a goal.
  validateSearch: (search: Record<string, unknown>): { scheda?: string; minuti?: number } => {
    const minutes = Number(search.minuti);
    return {
      scheda: text(search.scheda),
      minuti: Number.isInteger(minutes) && minutes >= 1 && minutes <= 240 ? minutes : undefined,
    };
  },
  component: lazyRouteComponent(() => import('./features/focus/FocusPage'), 'FocusPage'),
});

const incomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/incassi',
  component: lazyRouteComponent(() => import('./features/income/IncassiPage'), 'IncassiPage'),
});

const monthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mese',
  // ?mese=2026-10: the month to read; without it, the one just ended (or the current one).
  validateSearch: (search: Record<string, unknown>): { mese?: string } => ({
    mese: /^\d{4}-(0[1-9]|1[0-2])$/.test(String(search.mese)) ? String(search.mese) : undefined,
  }),
  component: lazyRouteComponent(() => import('./features/review/MonthPage'), 'MonthPage'),
});

const reviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/revisione',
  // ?settimana=2026-10-26: the Monday of the week to review.
  validateSearch: (search: Record<string, unknown>): { settimana?: string } => ({
    settimana: /^\d{4}-\d{2}-\d{2}$/.test(String(search.settimana))
      ? String(search.settimana)
      : undefined,
  }),
  component: lazyRouteComponent(() => import('./features/review/WeekReviewPage'), 'WeekReviewPage'),
});

const automationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/automazioni',
  component: lazyRouteComponent(
    () => import('./features/automation/AutomationsPage'),
    'AutomationsPage',
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/impostazioni',
  component: lazyRouteComponent(() => import('./features/settings/SettingsPage'), 'SettingsPage'),
});

const routeTree = rootRoute.addChildren([
  guestRoute.addChildren([loginRoute, registerRoute, forgotRoute]),
  resetRoute,
  verifyRoute,
  appRoute.addChildren([
    indexRoute,
    todayRoute,
    tasksRoute,
    moneyRoute,
    oldMoneyRoute,
    calendarRoute,
    focusRoute,
    incomeRoute,
    monthRoute,
    automationsRoute,
    reviewRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { session: undefined as unknown as SessionValue },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
